/**
 * Fleet connectivity view (#252, AST POC SC-1 "all units, live and historical"):
 * one row per managed (persistent / PLMN-hunt) modem across the org's devices —
 * live state, last scan / detection, 24 h attach success + uptime, position,
 * module temperature. Click-through to the device's Connectivity tab.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CentralEdgeModem } from '@simcheck/shared-types';
import { AppShell } from '@/components/layout/AppShell';
import { Badge, Button, Card } from '@/components/ui';
import { ResponsiveTable } from '@/components/ui/responsive-table';
import type { Column } from '@/components/ui/data-table';
import { edgeDeviceService } from '@/services/edgeDeviceService';
import { getConnectivityRollups } from '@/services/connectivityService';
import { useDateFormat } from '@/hooks/useDateFormat';
import { Activity, RefreshCw } from 'lucide-react';

interface Row {
  id: string; deviceId: string; deviceName: string; locationName: string; imei: string; label: string;
  mode: string; state: string; since: any; plmn: string | null; lastScan: any; targetSeen: boolean | null;
  uptimePct: number | null; attachSuccess: number | null; attempts: number; detaches: number; position: any; tempC: number | null;
}

export default function ConnectivityPage() {
  const navigate = useNavigate();
  const fmt = useDateFormat();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const devices = await edgeDeviceService.getDevices();
      const out: Row[] = [];
      await Promise.all(devices.map(async (d: any) => {
        const modems: CentralEdgeModem[] = await edgeDeviceService.getModems(d.id).catch(() => []);
        const managed = modems.filter((m) => m.connectivityMode);
        if (managed.length === 0) return;
        let rollups: any[] = [];
        try { rollups = (await getConnectivityRollups(d.id, { from: new Date(Date.now() - 86400_000), to: new Date() })).items; } catch { /* no data yet */ }
        for (const m of managed) {
          const imei = m.imei || m.modemId || '';
          const mine = rollups.filter((r) => r.modemImei === imei);
          const sum = (k: string) => mine.reduce((a, r) => a + (Number(r[k]) || 0), 0);
          const attempts = sum('attempts'); const attaches = sum('attaches');
          const st: any = m.connectivityStatus || {};
          out.push({
            id: `${d.id}|${imei}`, deviceId: d.id, deviceName: d.name || d.id, locationName: d.locationName || d.locationId || '—',
            imei, label: `${m.modemModelName || m.model || 'modem'} …${imei.slice(-6)}`, mode: m.connectivityMode!,
            state: st.state || 'unknown', since: st.since, plmn: st.plmn ?? null, lastScan: st.lastScan?.ts ?? null,
            targetSeen: st.lastScan ? !!st.lastScan.targetSeen : null,
            uptimePct: mine.length ? Math.round((mine.reduce((a: number, r: any) => a + Math.min(Number(r.attachedSec) || 0, 3600), 0) / (mine.length * 3600)) * 1000) / 10 : null,
            attachSuccess: attempts > 0 ? Math.round((attaches / attempts) * 1000) / 10 : null,
            attempts, detaches: sum('detaches'), position: st.position ?? null, tempC: st.moduleTempC ?? null,
          });
        }
      }));
      out.sort((a, b) => a.deviceName.localeCompare(b.deviceName) || a.label.localeCompare(b.label));
      setRows(out);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); const t = setInterval(load, 60_000); return () => clearInterval(t); }, []);

  const tone = (s: string) => (['attached', 'hunt_attached', 'hunt_terrestrial'].includes(s) ? 'default' : ['down', 'anchor_mismatch', 'modem_lost'].includes(s) ? 'destructive' : 'secondary');
  const columns: Column<Row>[] = useMemo(() => [
    { key: 'deviceName', header: 'Device', sortable: true, width: '14%', render: (v, r) => <div><div className="font-medium">{String(v)}</div><div className="text-xs text-muted-foreground">{r.locationName}</div></div> },
    { key: 'label', header: 'Modem', width: '14%', render: (v, r) => <div><div>{String(v)}</div><Badge variant="outline" className="text-[10px]">{r.mode === 'hunt' ? 'PLMN hunt' : 'persistent'}</Badge></div> },
    { key: 'state', header: 'State', sortable: true, width: '14%', render: (v, r) => <div><Badge variant={tone(String(v)) as any}><Activity className="mr-1 h-3 w-3" />{String(v).replace(/_/g, ' ')}</Badge>{r.since && <div className="text-[11px] text-muted-foreground">since {fmt.formatDateTime(typeof r.since === 'number' ? new Date(r.since) : r.since)}</div>}</div> },
    { key: 'plmn', header: 'PLMN', width: '8%', render: (v) => <span className="font-mono text-xs">{v ? String(v) : '—'}</span> },
    { key: 'lastScan', header: 'Last scan', width: '14%', render: (v, r) => v ? <span className="text-xs">{fmt.formatDateTime(typeof v === 'number' ? new Date(v) : (v as any))} · {r.targetSeen ? 'target seen' : 'no target'}</span> : <span className="text-xs text-muted-foreground">—</span> },
    { key: 'uptimePct', header: 'Uptime 24h', sortable: true, width: '9%', render: (v) => <span>{v == null ? '—' : `${v}%`}</span> },
    { key: 'attachSuccess', header: 'Attach 24h', sortable: true, width: '10%', render: (v, r) => <span>{v == null ? '—' : `${v}%`} <span className="text-xs text-muted-foreground">({r.attempts} att, {r.detaches} det)</span></span> },
    { key: 'position', header: 'Position', width: '11%', render: (v: any) => v ? <span className="font-mono text-xs">{Number(v.lat).toFixed(4)}, {Number(v.lon).toFixed(4)}</span> : <span className="text-xs text-muted-foreground">—</span> },
    { key: 'tempC', header: 'Temp', width: '6%', render: (v) => <span className="text-xs">{v == null ? '—' : `${v} °C`}</span> },
  ], [fmt]);

  return (
    <AppShell>
      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Connectivity</h1>
            <p className="text-sm text-muted-foreground">Every modem running persistent-attach or PLMN-hunt mode — live state and the last 24 hours.</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw className="mr-1 h-3.5 w-3.5" />Refresh</Button>
        </div>
        <Card padding="lg">
          <ResponsiveTable<Row>
            data={rows}
            columns={columns}
            keyExtractor={(r) => r.id}
            loading={loading}
            emptyMessage="No modem is in a connectivity mode yet. Enable one on a device's Modems tab."
            onRowClick={(r) => navigate(`/edges/${r.deviceId}?tab=connectivity`)}
            paginated
            defaultPageSize={25}
          />
        </Card>
      </div>
    </AppShell>
  );
}
